import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Home, Rss, MessageSquare, Store, Layers, FileText, Trophy, Sparkles,
  Coins, MessageCircle, Wallet, Users, Settings, Shield, User, ChevronDown, ChevronsLeft, X,
} from 'lucide-react';
// Sidebar supports collapse/expand via `collapsed` prop (persisted in Layout).
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../hooks/useApi';
import { formatNumber } from '../../lib/format';
import NuyIcon from '../common/NuyIcon';

const ICONS = {
  home: Home, feed: Rss, forums: MessageSquare, marketplace: Store, resources: Layers,
  blog: FileText, rankings: Trophy, plus: Sparkles, coin: Coins, chat: MessageCircle, members: Users,
  wallet: Wallet, profile: User, admin: Shield,
};

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) {
  const { isAuthed, user } = useAuth();
  const { data: site, isLoading: siteLoading } = usePublicSettings();
  const cleanUrl = (u) => (u && /^(https?:\/\/|\/)[^\s,]+$/.test(String(u).trim()) ? String(u).trim() : null);
  const logoUrl = cleanUrl(site?.logo_url);
  const logoIconUrl = cleanUrl(site?.logo_icon_url) || cleanUrl(site?.favicon_url);
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  const isActive = (href) => (href === '/' ? location.pathname === '/' : location.pathname.startsWith(href));

  const nav = [
    { type: 'link', href: '/', label: 'Home', icon: 'home' },
    { type: 'link', href: '/feed', label: 'Feed', icon: 'feed' },
    { type: 'group', href: '/forums', label: 'Forums', icon: 'forums', children: [
      ['/forums', 'All Forums'], ['/forums/new', 'New Thread'],
    ]},
    { type: 'group', href: '/marketplace', label: 'Marketplace', icon: 'marketplace', children: [
      ['/marketplace', 'Browse'], ['/marketplace/new', 'Sell a Product'], ['/marketplace/orders', 'My Orders'], ['/cart', 'Cart'],
    ]},
    { type: 'group', href: '/resources', label: 'Resources', icon: 'resources', children: [
      ['/resources', 'Browse'], ['/resources/new', 'Upload'], ['/resources/mine', 'My Resources'],
    ]},
    ...(isAuthed ? [{ type: 'group', href: '/profile', label: 'Profile', icon: 'profile', children: [
      [`/user/${user?.username}`, 'View Profile'], ['/messages', 'Messages'], ['/wallet', 'NUY Wallet'], ['/affiliate', 'Affiliates'], ['/settings', 'Settings'],
    ]}] : []),
    { type: 'link', href: '/blog', label: 'Blog', icon: 'blog' },
    { type: 'link', href: '/rankings', label: 'Rankings', icon: 'rankings' },
    { type: 'link', href: '/users', label: 'Members', icon: 'members' },
    { type: 'link', href: '/plus', label: 'Plus', icon: 'plus' },
    { type: 'link', href: '/nuy-coin', label: 'Nuy Coin', icon: 'coin' },
    { type: 'link', href: '/chat', label: 'Chat', icon: 'chat' },
  ];

  const toggleGroup = (href) => setOpenGroups((s) => ({ ...s, [href]: !s[href] }));

  const renderIcon = (key) => {
    const Ic = ICONS[key] || Home;
    return <Ic className="gx-nav-ic" strokeWidth={1.6} />;
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/70 backdrop-blur-sm md:hidden ${mobileOpen ? '' : 'hidden'}`}
        onClick={onCloseMobile}
      />
      <aside className={`gx-sidebar ${mobileOpen ? 'gx-mobile-open' : ''}`} data-testid="sidebar">
        <div className="gx-sidebar-head">
          <Link to="/" className="gx-logo-wrap" onClick={onCloseMobile} data-testid="sidebar-logo">
            <span className="gx-logo-collapsed-only">
              {logoIconUrl ? <img src={logoIconUrl} className="gx-logo-icon-img" alt={site?.site_name || 'NUYED'} /> : <span className="gx-logo-badge">N</span>}
            </span>
            {logoUrl ? (
              <img src={logoUrl} className="gx-logo-img gx-sidebar-label" alt={site?.site_name || 'NUYED'} />
            ) : siteLoading ? (
              <span className="gx-sidebar-label gx-logo-skeleton" aria-hidden="true" />
            ) : (
              <span className="gx-sidebar-label gx-logo-expanded">
                <span className="gx-logo-badge">N</span>
                <span className="gx-logo-word">{site?.site_name || 'NUYED'}</span>
              </span>
            )}
          </Link>
          <button className="gx-collapse-btn" onClick={onToggleCollapse} title="Collapse / expand" data-testid="sidebar-collapse-btn">
            <ChevronsLeft className="gx-collapse-ic" />
          </button>
          <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl border border-white/[0.08] bg-white/[0.03] text-gx-muted hover:text-gx-cyan hover:border-gx-cyan/40 transition flex-shrink-0" onClick={onCloseMobile} title="Close menu" data-testid="sidebar-mobile-close">
            <X className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <nav className="gx-sidebar-nav" data-testid="sidebar-nav">
          {nav.map((item) => {
            if (item.type === 'link') {
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === '/'}
                  onClick={onCloseMobile}
                  className={({ isActive: a }) => `gx-nav-link ${a ? 'is-active' : ''}`}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <span className="gx-nav-ic-wrap">{renderIcon(item.icon)}</span>
                  <span className="gx-sidebar-label gx-nav-label">{item.label}</span>
                  <span className="gx-nav-tip">{item.label}</span>
                </NavLink>
              );
            }
            const groupActive = isActive(item.href) || openGroups[item.href];
            return (
              <div key={item.href} className={`gx-nav-item gx-has-sub ${groupActive ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`gx-nav-link gx-sub-toggle ${isActive(item.href) ? 'is-active' : ''}`}
                  onClick={() => toggleGroup(item.href)}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <span className="gx-nav-ic-wrap">{renderIcon(item.icon)}</span>
                  <span className="gx-sidebar-label gx-nav-label">{item.label}</span>
                  <ChevronDown className="gx-sidebar-label gx-sub-chevron" />
                  <span className="gx-nav-tip">{item.label}</span>
                </button>
                <div className="gx-submenu">
                  <span className="gx-submenu-title">{item.label}</span>
                  {item.children.map(([href, label]) => (
                    <NavLink
                      key={href}
                      to={href}
                      end
                      onClick={onCloseMobile}
                      className={({ isActive: a }) => `gx-submenu-link ${a ? 'is-active' : ''}`}
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}

          {isAuthed && ['admin', 'mod', 'moderator'].includes(user?.role) && (
            <>
              <div className="gx-nav-divider"><span className="gx-sidebar-label gx-nav-section">Admin</span></div>
              <NavLink to="/admin" onClick={onCloseMobile} className={({ isActive: a }) => `gx-nav-link gx-nav-admin ${a ? 'is-active' : ''}`} data-testid="sidebar-nav-admin">
                <span className="gx-nav-ic-wrap">{renderIcon('admin')}</span>
                <span className="gx-sidebar-label gx-nav-label">Admin Panel</span>
                <span className="gx-nav-tip">Admin Panel</span>
              </NavLink>
            </>
          )}
        </nav>

        {isAuthed && (
          <Link to="/wallet" className="gx-sidebar-label gx-wallet-card" onClick={onCloseMobile} data-testid="sidebar-wallet-card">
            <span className="gx-wallet-ic"><NuyIcon className="w-[18px] h-[18px]" /></span>
            <span className="gx-wallet-meta">
              <span className="gx-wallet-label">NUY Balance</span>
              <span className="gx-wallet-balance" data-testid="sidebar-gax-balance">{formatNumber(user.gax_balance)}</span>
            </span>
            <svg className="gx-wallet-arrow" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
          </Link>
        )}
      </aside>
    </>
  );
}
